import React, { useState, useEffect } from 'react'
import { listForgeAccess, grantForgeAccess, revokeForgeAccess } from '../services/forgeAccessApi'
import './Access.css'

function Access() {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [emailInput, setEmailInput] = useState('')
  const [grantError, setGrantError] = useState(null)
  const [granting, setGranting] = useState(false)
  const [grantSuccess, setGrantSuccess] = useState(null)
  const [revokeConfirm, setRevokeConfirm] = useState(null) // email to revoke
  const [revoking, setRevoking] = useState(false)

  useEffect(() => {
    loadAccess()
  }, [])

  const loadAccess = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listForgeAccess()
      setEntries(Array.isArray(data) ? data : [])
    } catch (err) {
      setError('Failed to load access list.')
    } finally {
      setLoading(false)
    }
  }

  const handleGrant = async () => {
    const email = emailInput.trim()
    if (!email) {
      setGrantError('Email is required.')
      return
    }
    if (!email.includes('@')) {
      setGrantError('Enter a valid email address.')
      return
    }
    setGrantError(null)
    setGrantSuccess(null)
    setGranting(true)
    try {
      await grantForgeAccess(email)
      setEmailInput('')
      setGrantSuccess(email)
      await loadAccess()
    } catch (err) {
      setGrantError(err?.response?.data?.error || 'Failed to grant access.')
    } finally {
      setGranting(false)
    }
  }

  const handleRevokeConfirmed = async () => {
    if (!revokeConfirm) return
    setRevoking(true)
    try {
      await revokeForgeAccess(revokeConfirm)
      setRevokeConfirm(null)
      await loadAccess()
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to revoke access.')
      setRevokeConfirm(null)
    } finally {
      setRevoking(false)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      })
    } catch {
      return dateStr
    }
  }

  return (
    <div className="access">
      <div className="access-header">
        <h2>Forge Access</h2>
        <p className="access-subtitle">
          Manage who can use the Forge publishing tools in the Ditto MCP connector.
        </p>
      </div>

      <div className="access-grant">
        <h3>Grant access</h3>
        <div className="access-grant-form">
          <input
            type="email"
            placeholder="user@drips.com"
            value={emailInput}
            onChange={(e) => { setEmailInput(e.target.value); setGrantError(null); setGrantSuccess(null) }}
            className="access-email-input"
            onKeyDown={(e) => e.key === 'Enter' && handleGrant()}
            disabled={granting}
          />
          <button
            className="access-grant-btn"
            onClick={handleGrant}
            disabled={granting}
          >
            {granting ? 'Granting…' : 'Grant access'}
          </button>
        </div>
        {grantError && <div className="access-grant-error">{grantError}</div>}
        {grantSuccess && (
          <div className="access-grant-success">
            Access granted to <strong>{grantSuccess}</strong>.{' '}
            Ask them to reconnect the Ditto connector in Claude to see the Forge tools.
          </div>
        )}
      </div>

      {error && <div className="error-message">Error: {error}</div>}

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading access list…</p>
        </div>
      ) : (
        <div className="access-table-wrapper">
          {entries.length === 0 ? (
            <p className="access-empty">No users have Forge access yet.</p>
          ) : (
            <table className="access-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Granted by</th>
                  <th>Date granted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.email}>
                    <td className="access-email">{entry.email}</td>
                    <td>{entry.grantedBy || '—'}</td>
                    <td>{formatDate(entry.grantedAt)}</td>
                    <td>
                      <button
                        className="access-revoke-btn"
                        onClick={() => setRevokeConfirm(entry.email)}
                        disabled={revoking}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {revokeConfirm && (
        <div className="modal-overlay">
          <div className="access-revoke-modal">
            <h3>Revoke access</h3>
            <p>Remove Forge access for <strong>{revokeConfirm}</strong>?</p>
            <p className="access-revoke-note">They will lose access immediately on the next Forge API call.</p>
            <div className="access-revoke-actions">
              <button
                className="access-cancel-btn"
                onClick={() => setRevokeConfirm(null)}
                disabled={revoking}
              >
                Cancel
              </button>
              <button
                className="access-revoke-confirm-btn"
                onClick={handleRevokeConfirmed}
                disabled={revoking}
              >
                {revoking ? 'Revoking…' : 'Revoke'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Access
